// Investigate's investigators: model runs in the background, each owning one
// job in a scope (organize its findings into cases, dig a case, double-check
// a set-aside case, check findings the backend never did, ask the standards
// about verdicts that changed, draft standard edits), each saying what it is
// reading as it goes and what it found when it ends.
//
// One swarm per scope, kept for the page: leaving the Reviews lens does not
// stop it; a reload does, and what investigators found stays in the store.
// Nothing runs until the reader starts the swarm. The scope's coordinator (a
// chat, lib/investigateCoordinator.js) dispatches investigators and shows
// them in its chat the way the chat shows its own sub-agents, so each keeps a
// live slot in that shape: what it is doing, its tool calls, its transcript.
// Everything an investigator finds is written to the scope's investigation
// (lib/investigateStore.js), which the tab, the case boards and the chats all
// read. What they spend is counted where the reader sees it, and Stop stops
// them all.
import { useSyncExternalStore } from "react";
import { mirrorTranscript, refreshMirror } from "./agentTranscript.js";
import { buildCases, isProvisional, unorganized } from "./investigateCases.js";
import { GROUP_LABEL, SET_ASIDE, placeFindings, reasonLabel } from "./investigateFacts.js";
import { askStandardsHere, checkSession, digBrief, digCase, organizePass, suggestStandards } from "./investigateAgent.js";
import {
    addCaseNotes, investigationOf, markOrganized, newId, noteSpend, putAsked, putCases, putChecks, putCorrections, putSuggestions, updateCase,
} from "./investigateStore.js";
import { openCaseOnWorkspace, pinCaseNotes } from "./investigateWorkspace.js";
import { caseCanvasId, casesCanvasesWith, traceCanvasKept } from "./traceCanvasStore.js";
import { severityRank, verdictKey } from "./triageLite.js";

// What a swarm organizes when it starts: the groups a person reads first.
export const ORGANIZE_GROUPS = ["needs_you", "refused", "slipped", "unsettled"];
// Investigators at work at once; the rest wait their turn.
export const SWARM_CONCURRENCY = 3;
export const CHECK_MAX = 24;
// Investigators a swarm keeps, the newest.
const KEPT = 80;
const TRAIL_MAX = 30;
const SLOT_TOOLS = 6;
const PREVIEW_TAIL = 240;
const LOUD = 3; // high and above
// How long updates gather before the coordinator is told, so a burst of
// results reaches it as one message.
export const UPDATE_GATHER_MS = 2500;

const str = (v) => (v == null ? "" : String(v));
const clip = (v, n) => {
    const t = str(v).replace(/\s+/g, " ").trim();
    return t.length > n ? `${t.slice(0, n - 3)}...` : t;
};
const usd = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
export const KIND_LABEL = {
    organize: "Organize",
    dig: "Dig",
    double_check: "Double-check",
    check: "Check",
    ask: "Ask the standards",
    draft: "Draft standards",
};

// -- the tab's side ----------------------------------------------------------------

const contexts = new Map();
// What the tab holds for a scope: { pool, map, verdicts, standards, start,
// statuses, window, nameOf, scopeLabel }. Investigators read it when they
// start, so one that starts after an organize sees the cases it wrote. A
// scope that forms its cases another way (the case inbox) says so: `build()`
// gives its cases as buildCases shapes them, `keep(view)` keeps one before
// anything is written to it (returning its id), `pin` has every dig pin its
// notes on its sessions, and `wave(built)` is what a Start with no chat to
// coordinate it sends.
export function provideInvestigateContext(scopeKey, ctx) {
    contexts.set(scopeKey, ctx);
}

// The scope as an investigator sees it: every finding placed, and the cases.
export function snapshotOf(scopeKey) {
    const ctx = contexts.get(scopeKey);
    if (!ctx) return null;
    const investigation = investigationOf(scopeKey);
    const placed = placeFindings(ctx.pool || [], {
        verdicts: ctx.verdicts || {}, checks: investigation.checks, corrections: investigation.corrections, asked: investigation.asked,
        standards: (ctx.standards || []).length, start: ctx.start ?? null, statuses: ctx.statuses || {},
    });
    const built = ctx.build ? ctx.build({ placed, investigation }) : buildCases({ placed, map: ctx.map, investigation });
    return { ctx, investigation, placed, built };
}

// A case an investigator works on is a stored one: a provisional case is
// stored as it stands before anything is written to it.
export function keepCase(scopeKey, view) {
    const keep = contexts.get(scopeKey)?.keep;
    if (keep) return keep(view);
    if (!isProvisional(view.id)) return view.id;
    const id = newId("c:");
    putCases(scopeKey, [{ id, title: view.title, account: "", findings: view.findingIds, by: "investigate" }]);
    return id;
}

// -- state -----------------------------------------------------------------------------

const swarms = new Map();
const controllers = new Map();
// Each investigator's live slot, in the shape the chat draws a sub-agent in:
// { tools, preview, messages } and, while it runs, its agent; for one on a
// case, the case's sessions and findings (`case`) and where it last read
// (`reading`), which the chat draws as the session's card.
const slots = new Map();
const subscribers = new Set();
const watchers = new Map();
const coordinators = new Map();
const timers = new Map();
function watch(scopeKey) {
    for (const fn of watchers.get(scopeKey) || []) {
        try { fn(); } catch { /* a watcher's failure is its own */ }
    }
}
function emit(scopeKey) {
    for (const fn of subscribers) fn();
    watch(scopeKey);
}
function blank(scopeKey) {
    return {
        scopeKey, on: false, spentUsd: 0, coordinatorUsd: 0,
        investigators: [], updates: [], startedAt: 0,
    };
}
function patch(scopeKey, fn) {
    const cur = swarms.get(scopeKey) || blank(scopeKey);
    const next = fn(cur);
    if (!next || next === cur) return cur;
    swarms.set(scopeKey, { ...cur, ...next });
    emit(scopeKey);
    return swarms.get(scopeKey);
}
const patchInvestigator = (scopeKey, id, fn) => patch(scopeKey, (s) => ({
    investigators: s.investigators.map((i) => (i.id === id ? { ...i, ...fn(i) } : i)),
}));
const finished = (i) => i.status !== "queued" && i.status !== "running";
// The swarm keeps every investigator waiting or at work, and the newest of
// the rest.
function trim(list) {
    const over = list.length - KEPT;
    if (over <= 0) return list;
    const drop = new Set(list.filter(finished).sort((a, b) => (a.endedAt || a.at) - (b.endedAt || b.at)).slice(0, over).map((i) => i.id));
    for (const id of drop) slots.delete(id);
    return list.filter((i) => !drop.has(i.id));
}

export function swarmOf(scopeKey) {
    return swarms.get(scopeKey) || null;
}

export function useSwarm(scopeKey) {
    const read = () => swarms.get(scopeKey) || null;
    return useSyncExternalStore((fn) => { subscribers.add(fn); return () => subscribers.delete(fn); }, read, read);
}

// Called on every change to a scope's swarm, its investigators' slots
// included (a reply streaming in). Returns the unsubscribe.
export function onSwarmChange(scopeKey, fn) {
    if (!watchers.has(scopeKey)) watchers.set(scopeKey, new Set());
    watchers.get(scopeKey).add(fn);
    return () => watchers.get(scopeKey)?.delete(fn);
}

// The investigator at work on (or waiting for) a case, if one is.
export function workingOn(swarm, caseId) {
    return (swarm?.investigators || []).find((i) => i.caseId === caseId && !finished(i)) || null;
}

// -- the reader's controls ----------------------------------------------------------

export function startSwarm(scopeKey) {
    patch(scopeKey, (s) => ({ on: true, startedAt: s.startedAt || Date.now() }));
    pump(scopeKey);
    scheduleFlush(scopeKey);
    return swarmOf(scopeKey);
}

// Stops every investigator at work and drops the ones waiting.
export function stopSwarm(scopeKey) {
    for (const i of swarmOf(scopeKey)?.investigators || []) controllers.get(i.id)?.abort();
    patch(scopeKey, (s) => ({
        on: false,
        investigators: s.investigators.map((i) => (i.status === "queued" ? { ...i, status: "stopped", now: "Stopped", endedAt: Date.now() } : i)),
    }));
}

export function stopInvestigator(scopeKey, id) {
    const inv = swarmOf(scopeKey)?.investigators.find((i) => i.id === id);
    if (!inv) return;
    if (inv.status === "queued") patchInvestigator(scopeKey, id, () => ({ status: "stopped", now: "Stopped", endedAt: Date.now() }));
    else controllers.get(id)?.abort();
}

// New instructions for investigators at work, taken at their next turn the
// way a steered sub-agent takes them. Returns how many took it.
export function steerInvestigators(scopeKey, ids, message) {
    let n = 0;
    for (const id of ids || []) {
        const child = slots.get(id)?.child;
        if (!child || typeof child.steer !== "function") continue;
        try {
            child.steer(typeof message === "string" ? { role: "user", content: message, timestamp: Date.now() } : message);
            n += 1;
        } catch { /* a finished run takes nothing */ }
    }
    return n;
}

// -- dispatch ----------------------------------------------------------------------------

const describe = (scopeKey, item) => {
    const view = item.view || (item.caseId ? snapshotOf(scopeKey)?.built.byId.get(item.caseId) : null);
    if (item.kind === "organize") return "Findings into cases";
    if (item.kind === "draft") return "Standard edits from what slipped past";
    if (item.kind === "ask") return "Verdicts that changed, asked of the standards";
    const n = item.ids?.length || item.findingIds?.length || 0;
    return view?.title || (n ? `${n} finding${n === 1 ? "" : "s"}` : "a case");
};

// Investigators for `items` ({ kind, caseId?, findingIds?, question?,
// groups?, ids? }), queued in order. A case already waiting for the same
// kind is not queued twice, nor one at work unless the new one brings a
// question. `watched`: someone is waiting on these and will report them, so
// the coordinator is not told of them again. Returns the investigators made.
// A dig may bring the case as its sender sees it (`view`, stored under
// `caseId`, with the `window` its sessions are read in and how they are
// named, `nameOf`), for a case formed outside the scope's own snapshot (the
// case inbox); `pin` pins what it notes on its sessions, whether or not the
// case has a board.
export function dispatch(scopeKey, items, { by = "coordinator", front = false, watched = false } = {}) {
    const made = [];
    const list = swarmOf(scopeKey)?.investigators || [];
    const waiting = new Set(list.filter((i) => i.status === "queued").map((i) => `${i.kind}|${i.caseId || ""}`));
    const atWork = new Set(list.filter((i) => i.status === "running").map((i) => `${i.kind}|${i.caseId || ""}`));
    for (const item of items || []) {
        if (!KIND_LABEL[item.kind]) continue;
        const key = `${item.kind}|${item.caseId || ""}`;
        if (item.caseId && (waiting.has(key) || (atWork.has(key) && !str(item.question).trim()))) continue;
        waiting.add(key);
        made.push({
            id: newId("i:"), kind: item.kind, caseId: item.caseId || null, findingIds: item.findingIds || [], ids: item.ids || [], groups: item.groups || null,
            question: clip(item.question, 600), by, title: describe(scopeKey, item), watched,
            status: "queued", now: "Waiting its turn", trail: [], costUsd: 0, startedAt: 0, endedAt: 0, result: null, error: "", at: Date.now(),
            ...(item.view ? { view: item.view, window: item.window || null, nameOf: item.nameOf || null } : {}),
            ...(item.pin ? { pin: true } : {}),
        });
    }
    if (!made.length) return [];
    patch(scopeKey, (s) => ({ investigators: trim(front ? [...made, ...s.investigators] : [...s.investigators, ...made]) }));
    pump(scopeKey);
    return made;
}

// Whether someone is waiting on these investigators to report them.
export function setWatched(scopeKey, ids, watched) {
    const wanted = new Set(ids || []);
    patch(scopeKey, (s) => ({ investigators: s.investigators.map((i) => (wanted.has(i.id) && i.watched !== watched ? { ...i, watched } : i)) }));
}

// Resolves once every one of `ids` has finished, or, unless `all`, once none
// of them is at work and the rest cannot start (the swarm stopped). Resolves
// with { waiting }: how many were left waiting.
export function settled(scopeKey, ids, { all = false } = {}) {
    const wanted = new Set(ids || []);
    return new Promise((resolve) => {
        let off = () => {};
        let done = false;
        const check = () => {
            if (done) return;
            const s = swarmOf(scopeKey);
            const open = (s?.investigators || []).filter((i) => wanted.has(i.id) && !finished(i));
            const stuck = !all && !s?.on;
            if (!open.length || (stuck && !open.some((i) => i.status === "running"))) {
                done = true;
                off();
                resolve({ waiting: open.length });
            }
        };
        off = onSwarmChange(scopeKey, check);
        check();
    });
}

const running = (s) => s.investigators.filter((i) => i.status === "running");
const DUG_BEFORE_DRAFT = new Set(["organize", "dig", "double_check", "check"]);

// Starts what waits, while the swarm is on, up to the concurrency. An
// organize runs alone: what it writes changes every case.
// One case has one investigator at work at a time, and drafting waits for
// the digs: it drafts from what they found.
function pump(scopeKey) {
    const s = swarmOf(scopeKey);
    if (!s?.on) return;
    const at = running(s);
    if (at.some((i) => i.kind === "organize")) return;
    let started = 0;
    const cases = new Set(at.map((i) => i.caseId).filter(Boolean));
    for (const inv of s.investigators) {
        if (at.length + started >= SWARM_CONCURRENCY) break;
        if (inv.status !== "queued") continue;
        // An organize waits for what is at work to finish, and what is
        // behind it waits for the organize.
        if (inv.kind === "organize" && at.length + started > 0) break;
        if (inv.caseId && cases.has(inv.caseId)) continue;
        if (inv.kind === "draft" && s.investigators.some((i) => i !== inv && DUG_BEFORE_DRAFT.has(i.kind) && !finished(i))) continue;
        if (inv.caseId) cases.add(inv.caseId);
        started += 1;
        startInvestigator(scopeKey, inv);
        if (inv.kind === "organize") break;
    }
}

// -- an investigator at work -----------------------------------------------------------

const spend = (scopeKey, id, usdAmount) => {
    if (!(usdAmount > 0)) return;
    noteSpend(scopeKey, usdAmount);
    patch(scopeKey, (s) => ({
        spentUsd: s.spentUsd + usdAmount,
        coordinatorUsd: (s.coordinatorUsd || 0) + (id ? 0 : usdAmount),
        investigators: id ? s.investigators.map((i) => (i.id === id ? { ...i, costUsd: i.costUsd + usdAmount } : i)) : s.investigators,
    }));
};

// What the coordinator's own turns cost is counted with its investigators'.
export function noteCoordinatorSpend(scopeKey, usdAmount) {
    spend(scopeKey, null, Number(usdAmount) || 0);
}

// What a tool call reads, in the reader's words, naming a session by its
// summary where the tab knows one. The session's name comes last, so a line
// cut short keeps where it reads. `alone`: the row names the one session it
// reads already, so the line leaves it out.
export function nowOf(event, sessions, nameOf, { alone = false } = {}) {
    const args = event?.args || {};
    const session = (ref) => {
        const id = sessions?.find((s) => s.ref.toLowerCase() === str(ref).toLowerCase() || s.id === str(ref).toLowerCase())?.id || "";
        const name = id ? nameOf?.session?.(id) : null;
        return name ? `"${clip(name, 48)}"` : id ? `trace ${id.slice(0, 8)}` : "a trace";
    };
    const where = args.turn ? `T${args.turn}` : args.finding ? `around ${args.finding}` : args.message ? "around a message" : "";
    const kind = str(args.kind) || "records";
    if (alone) {
        switch (event?.toolName) {
        case "investigate_read_turns": return `Reading ${where || "the trace"}`;
        case "investigate_list_calls": return "Listing its tool calls";
        case "investigate_read_call": return `Reading a tool call${args.turn ? ` at T${args.turn}` : ""}`;
        case "investigate_search": return "Searching the trace";
        case "investigate_machine_records": return `Reading what ran on its machine (${kind})`;
        case "investigate_nearby_sessions": return "Looking for other traces on its machine";
        default: break;
        }
    }
    switch (event?.toolName) {
    case "investigate_read_turns": return `Reading ${where ? `${where} in ` : ""}${session(args.session)}`;
    case "investigate_list_calls": return `Listing the tool calls in ${session(args.session)}`;
    case "investigate_read_call": return `Reading a tool call${args.turn ? ` at T${args.turn}` : ""} in ${session(args.session)}`;
    case "investigate_search": return args.session ? `Searching ${session(args.session)}` : "Searching the case's traces";
    case "investigate_machine_records": return `Reading what ran on the machine (${kind}) for ${session(args.session)}`;
    case "investigate_nearby_sessions": return `Looking for other traces on the machine of ${session(args.session)}`;
    case "investigate_claim_origin": {
        const n = /\d+/.exec(str(args.claim))?.[0];
        return `Reading where ${n ? `Claim ${n}` : "a claim"} came from in the chat`;
    }
    case "return_result": return "Writing up what it found";
    default: return event?.toolName ? `Using ${event.toolName}` : "Thinking";
    }
}

const resultText = (result) => {
    const text = result?.content?.find?.((c) => c?.type === "text")?.text;
    return typeof text === "string" ? clip(text, 80) : "";
};

// The hooks a pass is run with: what it reads becomes the investigator's
// line and trail, its slot follows the run the way a sub-agent's row does,
// and what each reply cost is spent as it lands.
function hooks(scopeKey, id, sessions, nameOf) {
    const slot = slots.get(id);
    const onEvent = (event) => {
        if (slot) {
            refreshMirror(slot);
            if (event?.type === "turn_start") {
                slot.preview = "";
            } else if (event?.type === "tool_execution_start") {
                slot.tools = [...slot.tools, { name: event.toolName || "tool", status: "running", summary: "" }].slice(-SLOT_TOOLS);
            } else if (event?.type === "tool_execution_end") {
                const tools = slot.tools.slice();
                const last = tools[tools.length - 1];
                if (last) tools[tools.length - 1] = { ...last, status: event.isError ? "error" : "done", summary: resultText(event.result) || (event.isError ? "error" : "done") };
                slot.tools = tools;
            } else if (event?.type === "message_update" && event.assistantMessageEvent?.type === "text_delta") {
                slot.preview = `${slot.preview}${event.assistantMessageEvent.delta || ""}`.slice(-PREVIEW_TAIL);
            }
        }
        if (event?.type === "tool_execution_start") {
            const text = nowOf(event, sessions, nameOf);
            patchInvestigator(scopeKey, id, (i) => ({ now: text, trail: [...i.trail, { text, at: Date.now() }].slice(-TRAIL_MAX) }));
        } else if (event?.type === "message_end" && event.message?.role === "assistant" && Number(event.message?.usage?.cost?.total) > 0) {
            spend(scopeKey, id, Number(event.message.usage.cost.total));
        } else {
            watch(scopeKey);
        }
    };
    const onChildReady = (child) => {
        if (!slot) return;
        mirrorTranscript(slot, child);
        Object.defineProperty(slot, "child", { value: child, enumerable: false, configurable: true, writable: true });
        watch(scopeKey);
    };
    const onRead = ({ session, messageId }) => {
        if (!slot || !session) return;
        slot.reading = { session: str(session).toLowerCase(), messageId: messageId || null };
        watch(scopeKey);
    };
    return { onEvent, onChildReady, onRead };
}

// A case's sessions as an investigator's card shows them: each with its
// findings' cited messages, in the case's order.
function caseFocus(view, nameOf) {
    return {
        sessions: view.sessions.map((id) => {
            const items = view.items.filter((p) => str(p.finding.session).toLowerCase() === id);
            return {
                id,
                summary: nameOf?.session?.(id) || "",
                endpoint: items[0]?.finding.endpoint || null,
                findings: items.map((p) => ({ messageIds: p.finding.messages || [], title: p.finding.title || "", severity: p.finding.severity || "" })),
            };
        }),
    };
}

async function startInvestigator(scopeKey, inv) {
    const controller = new AbortController();
    controllers.set(inv.id, controller);
    slots.set(inv.id, { tools: [], preview: "", messages: [] });
    patchInvestigator(scopeKey, inv.id, () => ({ status: "running", startedAt: Date.now(), now: "Starting" }));
    let result = null;
    try {
        result = await RUNNERS[inv.kind](scopeKey, inv, controller.signal);
    } catch (error) {
        result = { error: str(error?.message || error) };
    } finally {
        controllers.delete(inv.id);
        const slot = slots.get(inv.id);
        if (slot?.child) {
            refreshMirror(slot);
            slot.child = null;
        }
    }
    const stopped = controller.signal.aborted;
    const ended = patchInvestigator(scopeKey, inv.id, () => ({
        status: stopped ? "stopped" : result?.error ? "failed" : "done",
        endedAt: Date.now(),
        now: stopped ? "Stopped" : result?.error ? `Failed: ${clip(result.error, 160)}` : "Done",
        result: result && !result.error ? result : null,
        error: result?.error || "",
    }));
    const watched = ended.investigators.find((i) => i.id === inv.id)?.watched;
    if (!stopped && !watched) queueUpdate(scopeKey, { kind: "result", investigator: inv.id, text: updateLine(scopeKey, inv.id) });
    pump(scopeKey);
}

// -- what each kind does ---------------------------------------------------------------

const viewOf = (snapshot, inv) => snapshot?.built.byId.get(inv.caseId)
    || snapshot?.built.cases.find((c) => c.findingIds.some((id) => inv.findingIds?.includes(id)));

async function runDig(scopeKey, inv, signal) {
    const snapshot = snapshotOf(scopeKey);
    const view = inv.view || viewOf(snapshot, inv);
    if (!view) return { error: "the case is no longer in view" };
    const caseId = keepCase(scopeKey, view);
    if (caseId !== inv.caseId) patchInvestigator(scopeKey, inv.id, () => ({ caseId }));
    const question = inv.kind === "double_check"
        ? [`The check set this case aside: ${GROUP_LABEL[view.group]}${view.items[0]?.reason ? ` (${reasonLabel(view.group, view.items[0].reason).toLowerCase()})` : ""}. Check whether that holds against what the sessions and the machine's records show; correct the check where they contradict it.`, inv.question].filter(Boolean).join(" ")
        : inv.question;
    // The refs the dig reads its sessions and leads by, for its live line.
    const brief = digBrief(view);
    const sessions = [...brief.sessions, ...brief.leads.map((l) => ({ id: l.id, ref: l.ref }))];
    const window = inv.window || snapshot?.ctx.window || null;
    const nameOf = inv.nameOf || snapshot?.ctx.nameOf || null;
    const slot = slots.get(inv.id);
    if (slot) slot.case = { caseId, ...caseFocus(view, nameOf) };
    const result = await digCase({
        view, question, priorities: (snapshot?.investigation || investigationOf(scopeKey)).priorities, window, nameOf, signal,
        ...hooks(scopeKey, inv.id, sessions, nameOf),
    });
    // Spent as the replies landed; what the pass counted beyond that (a
    // stand-in that reports its cost at the end) is spent now.
    const live = swarmOf(scopeKey)?.investigators.find((i) => i.id === inv.id)?.costUsd || 0;
    if (result.costUsd > live) spend(scopeKey, inv.id, result.costUsd - live);
    if (result.aborted || signal.aborted) {
        updateCase(scopeKey, caseId, (c) => ({ ...c, dig: { ...(c.dig || {}), status: c.dig?.status === "done" ? "done" : "stopped", at: Date.now() } }));
        return null;
    }
    if (!result.ok) {
        updateCase(scopeKey, caseId, (c) => ({ ...c, dig: { status: "failed", at: Date.now(), costUsd: result.costUsd, error: result.error, question: inv.question || "" } }));
        return { error: result.error || "the dig failed" };
    }
    // A dig speaks for the case as it now stands: its notes replace the last
    // dig's, and the reader's own and their chat's stay.
    updateCase(scopeKey, caseId, (c) => ({
        ...c,
        account: result.account || c.account,
        dig: { status: "done", at: Date.now(), costUsd: result.costUsd, question: inv.question || "", needsPerson: result.needsPerson, why: result.why, reads: result.reads },
        notes: c.notes.filter((n) => n.by !== "investigate"),
        related: result.related,
    }));
    addCaseNotes(scopeKey, caseId, result.notes.map((n) => ({ ...n, by: "investigate" })));
    const corrected = Object.keys(result.corrections).length;
    if (corrected) {
        putCorrections(scopeKey, Object.fromEntries(Object.entries(result.corrections).map(([id, k]) => [id, { ...k, caseId }])));
        dispatch(scopeKey, [{ kind: "ask" }], { by: "auto", front: true });
    }
    // A board already handed over for this case shows what the dig found, as
    // does one it was laid out on with other cases (its notes pinned on its
    // sessions there); one asked to pin it does so on its sessions, board or
    // not.
    const onBoard = traceCanvasKept(caseCanvasId(scopeKey, caseId));
    const pin = inv.pin || !!snapshot?.ctx.pin || casesCanvasesWith(scopeKey, caseId).length > 0;
    if (onBoard || pin) {
        // What was just written is the store's: a scope that forms its own
        // cases formed them before this dig wrote to the case.
        const stored = investigationOf(scopeKey).cases.find((c) => c.id === caseId);
        const seen = snapshotOf(scopeKey)?.built.byId.get(caseId) || view;
        const fresh = stored ? { ...seen, id: caseId, account: stored.account, dig: stored.dig, notes: stored.notes, related: stored.related } : seen;
        if (onBoard) openCaseOnWorkspace({ scopeKey, view: fresh, open: false, titleOf: nameOf?.session });
        else pinCaseNotes(scopeKey, fresh);
    }
    return {
        needsPerson: result.needsPerson, why: result.why, account: result.account,
        notes: result.notes.length, missed: result.notes.filter((n) => n.kind === "missed").length, corrected, related: result.related.length, dropped: result.dropped,
    };
}

async function runOrganize(scopeKey, inv, signal) {
    const snapshot = snapshotOf(scopeKey);
    if (!snapshot) return { error: "the scope is no longer in view" };
    const units = unorganized(snapshot.built, inv.groups || ORGANIZE_GROUPS);
    if (!units.length) { markOrganized(scopeKey); return { cases: 0, findings: 0 }; }
    const findings = units.reduce((n, u) => n + u.items.length, 0);
    patchInvestigator(scopeKey, inv.id, () => ({ title: `${findings.toLocaleString()} finding${findings === 1 ? "" : "s"} into cases` }));
    const existing = snapshot.built.cases.filter((c) => !c.provisional);
    const { onChildReady } = hooks(scopeKey, inv.id, [], snapshot.ctx.nameOf);
    const result = await organizePass({
        units, cases: existing, priorities: snapshot.investigation.priorities, nameOf: snapshot.ctx.nameOf, signal, onChildReady,
        onProgress: ({ done, total }) => patchInvestigator(scopeKey, inv.id, () => ({ now: `Reading batch ${Math.min(done + 1, total)} of ${total}` })),
    });
    spend(scopeKey, inv.id, result.costUsd);
    if (signal.aborted) return null;
    // Each written case takes its findings; one joining an existing case
    // keeps that case's own.
    const held = new Map(existing.map((c) => [c.id, investigationOf(scopeKey).cases.find((x) => x.id === c.id)]));
    putCases(scopeKey, result.cases.map((c) => {
        const prior = c.existing ? held.get(c.existing) : null;
        return prior
            ? { ...prior, findings: [...prior.findings, ...c.findings], matches: [...new Set([...(prior.matches || []), ...c.matches])], at: Date.now() }
            : { id: newId("c:"), title: c.title, account: c.account, findings: c.findings, matches: c.matches, by: "investigate" };
    }));
    markOrganized(scopeKey);
    return { cases: result.cases.length, findings, failed: result.failed };
}

async function runCheck(scopeKey, inv, signal) {
    const snapshot = snapshotOf(scopeKey);
    if (!snapshot) return { error: "the scope is no longer in view" };
    const wanted = new Set([...(inv.ids || []), ...(inv.findingIds || [])].map((id) => str(id).toLowerCase()));
    // What the model provider refused waits for a person, whatever asked for the check.
    const todo = snapshot.placed.filter((p) => wanted.has(str(p.finding.id).toLowerCase()) && !p.verdict && p.group !== "refused").slice(0, CHECK_MAX);
    if (!todo.length) return { checked: 0 };
    const bySession = new Map();
    for (const p of todo) {
        const s = str(p.finding.session).toLowerCase();
        if (!bySession.has(s)) bySession.set(s, []);
        bySession.get(s).push(p);
    }
    let checked = 0;
    for (const findings of bySession.values()) {
        if (signal.aborted) return null;
        const sessions = [{ id: str(findings[0].finding.session).toLowerCase(), ref: "S1" }];
        const slot = slots.get(inv.id);
        if (slot) slot.case = caseFocus({ sessions: [sessions[0].id], items: findings }, snapshot.ctx.nameOf);
        const result = await checkSession({ findings, window: snapshot.ctx.window, signal, ...hooks(scopeKey, inv.id, sessions, snapshot.ctx.nameOf) });
        const live = swarmOf(scopeKey)?.investigators.find((i) => i.id === inv.id)?.costUsd || 0;
        if (result.costUsd > live) spend(scopeKey, inv.id, result.costUsd - live);
        if (result.ok) { putChecks(scopeKey, result.checks); checked += Object.keys(result.checks).length; }
    }
    if (checked) dispatch(scopeKey, [{ kind: "ask" }], { by: "auto", front: true });
    return { checked };
}

// The verdicts no standard has answered as they stand: checked here, or
// corrected, and not yet asked of the standards in this browser.
function unaskedHere(snapshot) {
    const { placed, investigation, ctx } = snapshot;
    if (!(ctx.standards || []).length) return [];
    return placed.filter((p) => (p.source === "here" || p.source === "corrected")
        && (p.verdict?.verdict === "confirmed" || p.verdict?.verdict === "partial")
        && investigation.asked[str(p.finding.id).toLowerCase()]?.key !== verdictKey(p.verdict));
}

async function runAsk(scopeKey, inv, signal) {
    const snapshot = snapshotOf(scopeKey);
    if (!snapshot) return { error: "the scope is no longer in view" };
    const items = unaskedHere(snapshot).map((p) => ({ finding: p.finding, verdict: p.verdict }));
    if (!items.length) return { asked: 0, met: 0 };
    patchInvestigator(scopeKey, inv.id, () => ({ now: `Asking the standards about ${items.length} finding${items.length === 1 ? "" : "s"}` }));
    const result = await askStandardsHere({ items, standards: snapshot.ctx.standards, signal });
    spend(scopeKey, inv.id, result.costUsd);
    if (signal.aborted) return null;
    putAsked(scopeKey, result.asked);
    const met = Object.values(result.asked).filter((a) => a.answers.some((x) => x.outcome === "met")).length;
    return { asked: items.length, met };
}

async function runDraft(scopeKey, inv, signal) {
    const snapshot = snapshotOf(scopeKey);
    if (!snapshot) return { error: "the scope is no longer in view" };
    const slipped = snapshot.built.cases.filter((c) => c.group === "slipped" && c.dig?.needsPerson !== "no");
    if (!slipped.length) return { suggestions: 0 };
    const spare = snapshot.built.cases.filter((c) => SET_ASIDE.has(c.group) || c.dig?.needsPerson === "no").slice(0, 12);
    patchInvestigator(scopeKey, inv.id, () => ({ now: `Reading ${slipped.length} case${slipped.length === 1 ? "" : "s"} that slipped past` }));
    const result = await suggestStandards({ cases: slipped, spare, standards: snapshot.ctx.standards || [], signal, ...hooks(scopeKey, inv.id, [], snapshot.ctx.nameOf) });
    const live = swarmOf(scopeKey)?.investigators.find((i) => i.id === inv.id)?.costUsd || 0;
    if (result.costUsd > live) spend(scopeKey, inv.id, result.costUsd - live);
    if (signal.aborted) return null;
    if (!result.ok) return { error: result.error || "drafting failed" };
    putSuggestions(scopeKey, result.suggestions.map((s) => ({ ...s, id: newId("s:") })), { replace: true });
    return { suggestions: result.suggestions.length };
}

const RUNNERS = { dig: runDig, double_check: runDig, organize: runOrganize, check: runCheck, ask: runAsk, draft: runDraft };

// -- how an investigator reads -------------------------------------------------------

// The dig's answer, worded apart from the Needs you group (a standard met).
const NEEDS_WORD = { yes: "follow up", no: "can be let go", unsure: "unclear" };

// What an investigator came back with: `line` for its row, `text` for its
// result.
export function outcomeOf(inv) {
    if (inv.status === "failed") return { line: `failed: ${clip(inv.error, 120)}`, text: `Failed: ${clip(inv.error, 300)}` };
    if (inv.status === "stopped") return { line: "stopped", text: "Stopped before it finished." };
    if (inv.status !== "done") return null;
    const r = inv.result || {};
    if (inv.kind === "dig" || inv.kind === "double_check") {
        const needs = NEEDS_WORD[r.needsPerson] || "no answer";
        const extra = [r.missed ? `${r.missed} missed` : "", r.corrected ? `corrected the check on ${r.corrected}` : ""].filter(Boolean).join(", ");
        return {
            line: [needs, extra].filter(Boolean).join(" - "),
            text: [`${needs[0].toUpperCase()}${needs.slice(1)}.`, r.why || "", `${plural(r.notes || 0, "note")}${r.missed ? ` (${r.missed} on what the first passes missed)` : ""}.`, r.corrected ? `Corrected the check on ${plural(r.corrected, "finding")}.` : "", r.related ? `${plural(r.related, "related trace")}.` : ""].filter(Boolean).join(" "),
        };
    }
    if (inv.kind === "organize") return { line: `${plural(r.cases || 0, "case")} from ${plural(r.findings || 0, "finding")}`, text: `Folded ${plural(r.findings || 0, "finding")} into ${plural(r.cases || 0, "case")}${r.failed ? `; ${plural(r.failed, "batch", "batches")} failed` : ""}.` };
    if (inv.kind === "check") return { line: `${plural(r.checked || 0, "finding")} checked here`, text: `Checked ${plural(r.checked || 0, "finding")} under the verifier's rules.` };
    if (inv.kind === "ask") return { line: `${r.met || 0} of ${r.asked || 0} met a standard`, text: `Asked the standards about ${plural(r.asked || 0, "changed verdict")}; ${r.met || 0} met one.` };
    if (inv.kind === "draft") return { line: `${plural(r.suggestions || 0, "standard edit")} drafted`, text: `Drafted ${plural(r.suggestions || 0, "standard edit")}; they wait in the Investigate tab to be tried.` };
    return { line: "done", text: "Done." };
}

const NOTE_TONE = { missed: "danger", corrects: "danger", matters: "warn" };

// The session an investigator's card shows: the one it last read, else its
// case's first; the page centred where it read, else on its first finding;
// the findings pinned, and once it is done, what it noted there.
function focusOf(scopeKey, inv, slot) {
    const sessions = slot.case?.sessions || [];
    if (!sessions.length) return null;
    const at = sessions.find((s) => s.id === slot.reading?.session) || sessions[0];
    const reading = slot.reading?.session === at.id ? slot.reading.messageId : null;
    const caseId = inv.caseId || slot.case?.caseId || null;
    const notes = inv.status === "done" && caseId
        ? (investigationOf(scopeKey).cases.find((c) => c.id === caseId)?.notes || [])
            .filter((n) => n.session === at.id && n.messageId)
            .map((n) => ({ messageIds: [n.messageId], title: n.text, tone: NOTE_TONE[n.kind] || "calm" }))
        : [];
    return {
        session: at.id,
        summary: at.summary || "",
        endpointId: at.endpoint || null,
        around: reading || at.findings.flatMap((f) => f.messageIds)[0] || null,
        findings: at.findings,
        notes,
    };
}

// An investigator as a row of the chat's sub-agent batch.
export function batchItem(scopeKey, inv) {
    const slot = slots.get(inv.id) || {};
    const outcome = outcomeOf(inv);
    const open = !finished(inv);
    const focus = focusOf(scopeKey, inv, slot);
    return {
        id: inv.id,
        // The chat's row keeps its label whole on one line: short enough for
        // a dock's width.
        label: inv.caseId ? `${KIND_LABEL[inv.kind]}: ${clip(inv.title, 20)}` : KIND_LABEL[inv.kind],
        status: inv.status === "failed" ? "error" : inv.status,
        activity: inv.status === "running" ? inv.now : null,
        tools: slot.tools || [],
        preview: slot.preview || "",
        messages: slot.messages || [],
        summary: outcome?.line || (inv.status === "queued" ? "waiting its turn" : ""),
        result: outcome && inv.status === "done" ? outcome.text : null,
        abort: open ? () => stopInvestigator(scopeKey, inv.id) : null,
        ...(focus ? { focus } : {}),
    };
}

// Investigators `ids` as the chat's sub-agent batch.
export function batchState(scopeKey, ids, taskSummary = "") {
    const byId = new Map((swarmOf(scopeKey)?.investigators || []).map((i) => [i.id, i]));
    const items = (ids || []).map((id) => byId.get(id)).filter(Boolean).map((i) => batchItem(scopeKey, i));
    return {
        kind: "batch",
        taskSummary,
        total: items.length,
        done: items.filter((i) => i.status === "done").length,
        errored: items.filter((i) => i.status === "error").length,
        items,
    };
}

// -- what the coordinator is told ------------------------------------------------------

// One investigator's result, a line the coordinator reads.
export function updateLine(scopeKey, id) {
    const i = swarmOf(scopeKey)?.investigators.find((x) => x.id === id);
    if (!i) return "";
    const head = i.caseId ? `${i.id} ${KIND_LABEL[i.kind].toLowerCase()} of case ${i.caseId} "${clip(i.title, 80)}"` : `${i.id} ${KIND_LABEL[i.kind].toLowerCase()}`;
    if (i.status === "failed") return `${head} failed: ${clip(i.error, 160)}`;
    const r = i.result || {};
    if (i.kind === "dig" || i.kind === "double_check") {
        const needs = NEEDS_WORD[r.needsPerson] || "no answer";
        return `${head} finished: ${needs}${r.why ? ` (${clip(r.why, 200)})` : ""}. ${r.notes || 0} note${r.notes === 1 ? "" : "s"} (${r.missed || 0} missed)${r.corrected ? `, corrected the check on ${r.corrected} finding${r.corrected === 1 ? "" : "s"}` : ""}${r.related ? `, ${r.related} related session${r.related === 1 ? "" : "s"}` : ""}. Cost ${usd(i.costUsd)}.`;
    }
    if (i.kind === "organize") return `${head} finished: ${r.findings || 0} findings into ${r.cases || 0} cases.`;
    if (i.kind === "check") return `${head} finished: checked ${r.checked || 0} finding${r.checked === 1 ? "" : "s"} here.`;
    if (i.kind === "ask") return `${head} finished: asked about ${r.asked || 0}, ${r.met || 0} met a standard.`;
    if (i.kind === "draft") return `${head} finished: ${r.suggestions || 0} standard edit${r.suggestions === 1 ? "" : "s"} drafted.`;
    return `${head} finished.`;
}

// Tells the coordinator, gathering what lands close together into one
// message. `notify(text, { onCost })` resolves true once the coordinator took
// it; what it could not take is told again later. Nothing is told while the
// swarm is stopped: a coordinator's turn spends too.
export function bindCoordinator(scopeKey, notify) {
    if (notify) coordinators.set(scopeKey, notify);
    else coordinators.delete(scopeKey);
    scheduleFlush(scopeKey);
}

export const coordinatorBound = (scopeKey) => coordinators.has(scopeKey);

function scheduleFlush(scopeKey, ms = UPDATE_GATHER_MS) {
    if (timers.has(scopeKey) || !coordinators.has(scopeKey) || !(swarmOf(scopeKey)?.updates || []).length) return;
    timers.set(scopeKey, setTimeout(() => { timers.delete(scopeKey); flushUpdates(scopeKey); }, ms));
}

function queueUpdate(scopeKey, update) {
    patch(scopeKey, (s) => ({ updates: [...s.updates, { ...update, at: Date.now() }].slice(-40) }));
    scheduleFlush(scopeKey);
}

export function digestText(scopeKey, updates) {
    const s = swarmOf(scopeKey);
    const at = (s?.investigators || []).filter((i) => i.status === "running").length;
    const waiting = (s?.investigators || []).filter((i) => i.status === "queued").length;
    return [
        "<investigator_updates>",
        ...updates.map((u) => `- ${u.text}`),
        `Now: ${at} at work, ${waiting} waiting. Spent ${usd(s?.spentUsd)}.`,
        "</investigator_updates>",
        "Tell the operator, in a sentence or two, only what changed that matters to them; if nothing did, don't write to them. Dispatch what a result calls for next.",
    ].join("\n");
}

let flushing = new Set();
export async function flushUpdates(scopeKey) {
    const notify = coordinators.get(scopeKey);
    const s = swarmOf(scopeKey);
    const pending = s?.updates || [];
    if (!notify || !pending.length || flushing.has(scopeKey) || !s.on) return false;
    flushing.add(scopeKey);
    const sent = pending.slice();
    let ok = false;
    try {
        ok = Boolean(await notify(digestText(scopeKey, sent), { onCost: (cost) => noteCoordinatorSpend(scopeKey, cost) }));
        if (ok) patch(scopeKey, (cur) => ({ updates: cur.updates.filter((u) => !sent.includes(u)) }));
        return ok;
    } catch {
        return false;
    } finally {
        flushing.delete(scopeKey);
        // What landed meanwhile, or what could not be told (the person was
        // talking to the coordinator), is told next.
        scheduleFlush(scopeKey, ok ? UPDATE_GATHER_MS : UPDATE_GATHER_MS * 4);
    }
}

// -- choosing --------------------------------------------------------------------------

const loud = (c) => severityRank(c.severity) >= LOUD;

// The first wave when no coordinator plans it: organize, dig every case that
// needs a person, double-check the loudest set-aside ones, dig what slipped
// past and is loud or bears on a priority, then draft standard edits.
export function defaultWave(built, { doubleChecks = 2, slipped = 6 } = {}) {
    const fresh = built.cases.filter((c) => !c.dig);
    return [
        { kind: "organize" },
        ...fresh.filter((c) => c.group === "needs_you").map((c) => ({ kind: "dig", caseId: c.id, findingIds: c.findingIds })),
        ...fresh.filter((c) => SET_ASIDE.has(c.group) && c.group !== "not_an_action" && loud(c)).slice(0, doubleChecks).map((c) => ({ kind: "double_check", caseId: c.id, findingIds: c.findingIds })),
        ...fresh.filter((c) => c.group === "slipped" && (c.matches.length || loud(c))).slice(0, slipped).map((c) => ({ kind: "dig", caseId: c.id, findingIds: c.findingIds })),
        { kind: "draft" },
    ];
}

// Test seam.
export function _resetSwarmsForTests() {
    for (const c of controllers.values()) c.abort();
    for (const t of timers.values()) clearTimeout(t);
    controllers.clear();
    timers.clear();
    swarms.clear();
    slots.clear();
    contexts.clear();
    coordinators.clear();
    watchers.clear();
    flushing = new Set();
    for (const fn of subscribers) fn();
}
